/**
 * Content Manager - UI Preferences Store
 *
 * Zustand store for managing user preferences that persist across sessions.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// =============================================================================
// TYPES
// =============================================================================

interface RecentItem {
  id: string;
  type: 'seed' | 'output';
  title: string;
  timestamp: string;
}

// Date format options
export type DateFormatOption = 'dd/MM/yyyy' | 'MM/dd/yyyy' | 'yyyy-MM-dd';

// Calendar entry type filter
export type CalendarEntryType = 'output' | 'event' | 'awards' | 'deadline' | 'elevate';

export interface CalendarEntryTypeFilters {
  output: boolean;
  event: boolean;
  awards: boolean;
  deadline: boolean;
  elevate: boolean;
}

interface UIPreferencesState {
  // Onboarding
  hasCompletedOnboarding: boolean;
  onboardingStep: number;

  // Theme
  darkMode: boolean;

  // Recent items
  recentItems: RecentItem[];

  // View preferences
  defaultView: string;
  calendarWeekStartsOnMonday: boolean;
  showCompletedTasks: boolean;
  dateFormat: DateFormatOption;
  calendarEntryTypeFilters: CalendarEntryTypeFilters;

  // Content view preferences
  defaultSeedExpansion: 'collapsed' | 'expanded' | 'remember';
  showOutputPreview: boolean;
  compactMode: boolean;

  // Notification preferences
  showNotifications: boolean;
  soundEnabled: boolean;

  // Actions - Onboarding
  completeOnboarding: () => void;
  resetOnboarding: () => void;
  setOnboardingStep: (step: number) => void;
  nextOnboardingStep: () => void;

  // Actions - Theme
  setDarkMode: (enabled: boolean) => void;
  toggleDarkMode: () => void;

  // Actions - Recent items
  addRecentItem: (item: Omit<RecentItem, 'timestamp'>) => void;
  clearRecentItems: () => void;

  // Actions - View preferences
  setDefaultView: (view: string) => void;
  setCalendarWeekStart: (monday: boolean) => void;
  setShowCompletedTasks: (show: boolean) => void;
  setDateFormat: (format: DateFormatOption) => void;
  setCalendarEntryTypeFilter: (entryType: CalendarEntryType, enabled: boolean) => void;

  // Actions - Content view preferences
  setDefaultSeedExpansion: (expansion: 'collapsed' | 'expanded' | 'remember') => void;
  setShowOutputPreview: (show: boolean) => void;
  setCompactMode: (compact: boolean) => void;

  // Actions - Notifications
  setShowNotifications: (show: boolean) => void;
  setSoundEnabled: (enabled: boolean) => void;

  // Reset all preferences
  resetPreferences: () => void;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const MAX_RECENT_ITEMS = 10;

const DEFAULT_PREFERENCES = {
  hasCompletedOnboarding: false,
  onboardingStep: 0,
  darkMode: false,
  recentItems: [],
  defaultView: 'content',
  calendarWeekStartsOnMonday: true,
  showCompletedTasks: true,
  dateFormat: 'dd/MM/yyyy' as DateFormatOption,
  calendarEntryTypeFilters: {
    output: true,
    event: true,
    awards: true,
    deadline: true,
    elevate: true,
  } as CalendarEntryTypeFilters,
  defaultSeedExpansion: 'collapsed' as const,
  showOutputPreview: true,
  compactMode: false,
  showNotifications: true,
  soundEnabled: false,
};

// =============================================================================
// STORE
// =============================================================================

export const useUIPreferencesStore = create<UIPreferencesState>()(
  persist(
    (set, get) => ({
      // Initial state
      ...DEFAULT_PREFERENCES,

      // ==========================================================================
      // ONBOARDING ACTIONS
      // ==========================================================================

      completeOnboarding: () => {
        set({ hasCompletedOnboarding: true });
      },

      resetOnboarding: () => {
        set({ hasCompletedOnboarding: false, onboardingStep: 0 });
      },

      setOnboardingStep: (step) => {
        set({ onboardingStep: step });
      },

      nextOnboardingStep: () => {
        set((state) => ({ onboardingStep: state.onboardingStep + 1 }));
      },

      // ==========================================================================
      // THEME ACTIONS
      // ==========================================================================

      setDarkMode: (enabled) => {
        set({ darkMode: enabled });
        // Apply to document
        if (enabled) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },

      toggleDarkMode: () => {
        const { darkMode } = get();
        get().setDarkMode(!darkMode);
      },

      // ==========================================================================
      // RECENT ITEMS ACTIONS
      // ==========================================================================

      addRecentItem: (item) => {
        set((state) => {
          // Remove existing entry for same item
          const filtered = state.recentItems.filter((r) => r.id !== item.id);

          // Add new item at the beginning
          const newItem: RecentItem = {
            ...item,
            timestamp: new Date().toISOString(),
          };

          // Keep only MAX_RECENT_ITEMS
          const updated = [newItem, ...filtered].slice(0, MAX_RECENT_ITEMS);

          return { recentItems: updated };
        });
      },

      clearRecentItems: () => {
        set({ recentItems: [] });
      },

      // ==========================================================================
      // VIEW PREFERENCES ACTIONS
      // ==========================================================================

      setDefaultView: (view) => {
        set({ defaultView: view });
      },

      setCalendarWeekStart: (monday) => {
        set({ calendarWeekStartsOnMonday: monday });
      },

      setShowCompletedTasks: (show) => {
        set({ showCompletedTasks: show });
      },

      setDateFormat: (format) => {
        set({ dateFormat: format });
      },

      setCalendarEntryTypeFilter: (entryType, enabled) => {
        set((state) => ({
          calendarEntryTypeFilters: {
            ...state.calendarEntryTypeFilters,
            [entryType]: enabled,
          },
        }));
      },

      // ==========================================================================
      // CONTENT VIEW PREFERENCES ACTIONS
      // ==========================================================================

      setDefaultSeedExpansion: (expansion) => {
        set({ defaultSeedExpansion: expansion });
      },

      setShowOutputPreview: (show) => {
        set({ showOutputPreview: show });
      },

      setCompactMode: (compact) => {
        set({ compactMode: compact });
      },

      // ==========================================================================
      // NOTIFICATION ACTIONS
      // ==========================================================================

      setShowNotifications: (show) => {
        set({ showNotifications: show });
      },

      setSoundEnabled: (enabled) => {
        set({ soundEnabled: enabled });
      },

      // ==========================================================================
      // RESET
      // ==========================================================================

      resetPreferences: () => {
        set(DEFAULT_PREFERENCES);
        document.documentElement.classList.remove('dark');
      },
    }),
    {
      name: 'content-manager-preferences',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// =============================================================================
// SELECTORS
// =============================================================================

export const selectHasCompletedOnboarding = (state: UIPreferencesState) =>
  state.hasCompletedOnboarding;
export const selectOnboardingStep = (state: UIPreferencesState) => state.onboardingStep;
export const selectDarkMode = (state: UIPreferencesState) => state.darkMode;
export const selectRecentItems = (state: UIPreferencesState) => state.recentItems;
export const selectDefaultView = (state: UIPreferencesState) => state.defaultView;
export const selectCalendarWeekStartsOnMonday = (state: UIPreferencesState) =>
  state.calendarWeekStartsOnMonday;
export const selectShowCompletedTasks = (state: UIPreferencesState) =>
  state.showCompletedTasks;
export const selectDateFormat = (state: UIPreferencesState) =>
  state.dateFormat;
export const selectCalendarEntryTypeFilters = (state: UIPreferencesState) =>
  state.calendarEntryTypeFilters;
export const selectDefaultSeedExpansion = (state: UIPreferencesState) =>
  state.defaultSeedExpansion;
export const selectShowOutputPreview = (state: UIPreferencesState) =>
  state.showOutputPreview;
export const selectCompactMode = (state: UIPreferencesState) => state.compactMode;
export const selectShowNotifications = (state: UIPreferencesState) =>
  state.showNotifications;
export const selectSoundEnabled = (state: UIPreferencesState) => state.soundEnabled;

export default useUIPreferencesStore;
